import { useMemo } from 'react'

import { Flex, Icon, InterfaceWarningIcon, Text } from '@grammarly/design-system'

import styles from './AgentError.module.scss'

/**
 * Props for AgentError component. Everything is optional, but at least one of the fields has to be set.
 */
type Props =
  | { title: string; content?: string; accessibilityLabel?: string }
  | { title?: string; content: string; accessibilityLabel?: string }
  | { title?: string; content?: string; accessibilityLabel: string }

/**
 * A component that displays an error state for agents with a warning icon and optional title/content.
 * Used as a fallback when an agent panel or agent-related functionality encounters an error.
 */
export function AgentError({ accessibilityLabel, title, content }: Props) {
  const resolvedAccessibilityLabel = useMemo(() => {
    return accessibilityLabel ?? title ?? content ?? ''
  }, [accessibilityLabel, title, content])

  return (
    <Flex className={styles.agentError} gap={1} justify="center" align="center" direction="column">
      <Icon
        icon={InterfaceWarningIcon}
        size="xlarge"
        accessibilityLabel={resolvedAccessibilityLabel}
      />

      {title && (
        <Text as="p" variant="heading-xsmall" align="center">
          {title}
        </Text>
      )}

      {content && (
        <Text as="p" variant="text-small" color="base-subdued" align="center">
          {content}
        </Text>
      )}
    </Flex>
  )
}
